import { gsap } from "gsap";
import { Coins, ShieldCheck, UserRound, type LucideIcon } from "lucide-react";
import { useRef } from "react";

import { MOTION_DURATION, MOTION_EASE, MOTION_STAGGER } from "@/shared/components/motion/motion.tokens";
import { useIllustrationLoop } from "@/shared/components/security/useIllustrationLoop";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

interface Requirement {
  key: "security" | "holder" | "balance";
  icon: LucideIcon;
  label: "first_card_diagram_security" | "first_card_diagram_holder" | "first_card_diagram_balance";
  /** Vertical centre of the node. */
  y: number;
  /** From the node to the side of the card where its part lands. */
  path: string;
}

const REQUIREMENTS: Requirement[] = [
  {
    key: "security",
    icon: ShieldCheck,
    label: "first_card_diagram_security",
    y: 28,
    path: "M106 28 C 150 28, 156 58, 194 58",
  },
  {
    key: "holder",
    icon: UserRound,
    label: "first_card_diagram_holder",
    y: 75,
    path: "M106 75 C 140 75, 160 75, 194 75",
  },
  {
    key: "balance",
    icon: Coins,
    label: "first_card_diagram_balance",
    y: 122,
    path: "M106 122 C 150 122, 156 92, 194 92",
  },
];

/** Seconds a dot takes to go from a requirement to the card. */
const TRAVEL = 0.85;

/**
 * Sends one requirement to the card: its dot travels the link, and on arrival the node is ticked and its part
 * lands on the card.
 */
function deliver(
  timeline: gsap.core.Timeline,
  link: SVGPathElement,
  dot: SVGCircleElement,
  tick: Element | null,
  part: Element | null,
): void {
  const length = link.getTotalLength();
  const travel = { at: 0 };
  const place = () => {
    const point = link.getPointAtLength(travel.at * length);
    dot.setAttribute("cx", String(point.x));
    dot.setAttribute("cy", String(point.y));
  };
  timeline
    .call(() => {
      travel.at = 0;
      place();
    })
    .to(dot, { opacity: 1, duration: MOTION_DURATION.quick })
    .to(travel, { at: 1, duration: TRAVEL, ease: "power1.inOut", onUpdate: place }, "<")
    .to(dot, { opacity: 0, duration: MOTION_DURATION.quick })
    .to(tick, { scale: 1, opacity: 1, duration: MOTION_DURATION.quick, ease: MOTION_EASE.pop }, "<")
    .to(part, { scale: 1, opacity: 1, duration: MOTION_DURATION.enter, ease: MOTION_EASE.pop }, "<");
}

/**
 * The first card being put together: the security method and the holder travel to an empty card slot and land
 * on it as its chip and its name, the card is issued (the slot fills), then the balance arrives and the card
 * is ready. The links draw once; the deliveries loop, one after the other, because the order is the message —
 * what has to exist before the card, and what comes after. Static (and complete) with reduced motion.
 */
function buildFirstCardLoop(svg: SVGSVGElement): gsap.core.Timeline {
  const q = <T extends Element>(selector: string) => svg.querySelector<T>(selector);
  const links = REQUIREMENTS.map(({ key }) => q<SVGPathElement>(`[data-link="${key}"]`));
  const dots = REQUIREMENTS.map(({ key }) => q<SVGCircleElement>(`[data-dot="${key}"]`));
  const ticks = REQUIREMENTS.map(({ key }) => q(`[data-tick="${key}"]`));
  const nodes = svg.querySelectorAll("[data-node]");
  const slot = q("[data-slot]");
  const face = q("[data-face]");
  const ready = q("[data-ready]");
  const chip = q('[data-part="security"]');
  const name = q('[data-part="holder"]');
  const coin = q('[data-part="balance"]');

  const master = gsap.timeline({ paused: true });
  if (links.some((link) => !link) || dots.some((dot) => !dot)) return master;

  const parts = [chip, name, coin, ready];
  // One origin per element, set once: changing it later makes GSAP shift the SVG element to compensate.
  gsap.set([...ticks, chip, coin, ready], { scale: 0, opacity: 0, transformOrigin: "50% 50%" });
  gsap.set(name, { scale: 0, opacity: 0, transformOrigin: "0% 50%" });
  gsap.set(face, { opacity: 0, scale: 0.94, transformOrigin: "50% 50%" });
  gsap.set(dots, { opacity: 0 });

  master.fromTo(
    nodes,
    { opacity: 0, x: -6 },
    {
      opacity: 1,
      x: 0,
      duration: MOTION_DURATION.enter,
      ease: MOTION_EASE.enter,
      stagger: MOTION_STAGGER.loose,
    },
  );
  master.fromTo(
    slot,
    { opacity: 0, scale: 0.94, transformOrigin: "50% 50%" },
    { opacity: 1, scale: 1, duration: MOTION_DURATION.enter, ease: MOTION_EASE.enter },
    0.15,
  );
  links.forEach((link, index) => {
    if (!link) return;
    const length = link.getTotalLength();
    master.fromTo(
      link,
      { strokeDasharray: length, strokeDashoffset: length },
      { strokeDashoffset: 0, duration: MOTION_DURATION.enter, ease: MOTION_EASE.enter },
      0.3 + index * MOTION_STAGGER.loose,
    );
  });

  const loop = gsap.timeline({ repeat: -1, repeatDelay: 0.4 });
  const [securityLink, holderLink, balanceLink] = links as SVGPathElement[];
  const [securityDot, holderDot, balanceDot] = dots as SVGCircleElement[];
  const [securityTick, holderTick, balanceTick] = ticks;

  deliver(loop, securityLink, securityDot, securityTick, chip);
  deliver(loop, holderLink, holderDot, holderTick, name);
  // Both in place: the card is issued. The flow's one loud moment.
  loop.to(face, { opacity: 1, scale: 1, duration: MOTION_DURATION.slow, ease: MOTION_EASE.pop }, "+=0.2");
  deliver(loop, balanceLink, balanceDot, balanceTick, coin);
  loop
    .to(ready, { scale: 1, opacity: 1, duration: MOTION_DURATION.enter, ease: MOTION_EASE.pop }, "+=0.1")
    .to({}, { duration: 1.6 })
    .to([...ticks, ...parts, face], {
      opacity: 0,
      duration: MOTION_DURATION.enter,
      ease: MOTION_EASE.enter,
    })
    .set([...ticks, ...parts], { scale: 0 })
    .set(face, { scale: 0.94 });

  master.add(loop, "+=0.2");
  return master;
}

/** A requirement's glyph, drawn as a nested SVG so it can sit inside the diagram's coordinates. */
function Glyph({
  icon: Icon,
  x,
  y,
  className,
}: {
  icon: LucideIcon;
  x: number;
  y: number;
  className?: string;
}) {
  return <Icon x={x} y={y} width={12} height={12} strokeWidth={2} className={className} aria-hidden="true" />;
}

export function FirstCardDiagram({ className }: { className?: string }) {
  const { t } = useTranslations("cards");
  const ref = useRef<SVGSVGElement>(null);
  useIllustrationLoop(ref, buildFirstCardLoop);

  return (
    <svg
      ref={ref}
      viewBox="0 0 340 150"
      role="img"
      aria-label={t("first_card_diagram_aria")}
      className={cn("h-auto w-full", className)}
    >
      {REQUIREMENTS.map(({ key, path }) => (
        <path
          key={`link-${key}`}
          data-link={key}
          d={path}
          fill="none"
          className="stroke-primary/50"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      ))}

      {/* What the card needs: two things before it exists, one after. */}
      {REQUIREMENTS.map(({ key, icon, label, y }) => (
        <g key={`node-${key}`} data-node>
          <rect x="6" y={y - 14} width="100" height="28" rx="14" className="fill-card stroke-border" />
          <circle cx="22" cy={y} r="9" className="fill-primary/10" />
          <Glyph icon={icon} x={16} y={y - 6} className="text-primary" />
          <text x="37" y={y + 3.5} className="fill-foreground font-sans text-[10px] font-medium">
            {t(label)}
          </text>
          <g data-tick={key}>
            <circle cx="104" cy={y - 12} r="6" className="fill-success" />
            <path
              d={`M101.4 ${y - 12} L103.3 ${y - 10} L106.6 ${y - 14}`}
              fill="none"
              className="stroke-success-foreground"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </g>
      ))}

      {REQUIREMENTS.map(({ key }) => (
        <circle key={`dot-${key}`} data-dot={key} r="3.5" className="fill-primary" cx="-10" cy="-10" />
      ))}

      {/* The place the card will take, and what lands on it before it is issued. */}
      <rect
        data-slot
        x="196"
        y="37"
        width="128"
        height="76"
        rx="9"
        className="fill-primary/5 stroke-primary/40"
        strokeWidth="1.5"
        strokeDasharray="5 4"
      />

      {/* What lands on the slot before the card exists: the security method as its chip, the holder as its name. */}
      <g data-part="security">
        <rect x="208" y="50" width="20" height="15" rx="3.5" className="fill-card stroke-primary/60" />
        <Glyph icon={ShieldCheck} x={212} y={51.5} className="text-primary" />
      </g>
      <g data-part="holder">
        <rect x="208" y="92" width="58" height="4.5" rx="2.25" className="fill-primary/60" />
        <rect x="208" y="100" width="34" height="3.5" rx="1.75" className="fill-primary/35" />
      </g>

      {/* The issued card, covering the slot with the same two parts in white. */}
      <g data-face>
        {/* The stroke covers the slot's dashed outline, which would otherwise fray the card's edge. */}
        <rect
          x="196"
          y="37"
          width="128"
          height="76"
          rx="9"
          className="fill-primary stroke-primary"
          strokeWidth="2"
        />
        <rect x="208" y="50" width="20" height="15" rx="3.5" className="fill-primary-foreground/25" />
        <Glyph icon={ShieldCheck} x={212} y={51.5} className="text-primary-foreground" />
        <rect x="208" y="92" width="58" height="4.5" rx="2.25" className="fill-primary-foreground" />
        <rect x="208" y="100" width="34" height="3.5" rx="1.75" className="fill-primary-foreground/60" />
      </g>
      <g data-part="balance">
        <circle cx="306" cy="57" r="9" className="fill-primary-foreground/20" />
        <Glyph icon={Coins} x={300} y={51} className="text-primary-foreground" />
      </g>
      <g data-ready>
        <circle cx="322" cy="39" r="8" className="fill-success" />
        <path
          d="M318.4 39.2 L321 41.8 L325.8 36.4"
          fill="none"
          className="stroke-success-foreground"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      <text
        x="260"
        y="134"
        textAnchor="middle"
        className="fill-muted-foreground font-sans text-[9px] font-medium"
      >
        {t("first_card_diagram_card")}
      </text>
    </svg>
  );
}
